import type { GTooltipCardData } from '~/presentation/ui';

import { BlockStack, Card, InlineStack, Text, Badge, Box, Icon } from '@shopify/polaris';
import { AnalyticSwitchMetric } from './AnalyticMetricTab/AnalyticSwitchMetric';
import { ChevronRightIcon } from '@shopify/polaris-icons';
import { GTooltipCard } from '~/presentation/ui';
import { useTranslation } from 'react-i18next';
import { useToggle } from '~/hooks/useToggle';
import { cls } from '~/utils';

export interface CardAnalyticWrapperProps {
  title: string;
  tooltip?: GTooltipCardData;
  badge?: {
    isNew?: boolean;
    info?: {
      content: React.ReactNode;
    };
  };
  positionMetricShowing?: number;
  isEmptyMetricData?: boolean;
  onTitleClick?: () => void;
  headerContent?: React.ReactNode;
  children: React.ReactNode;
}

export const CardAnalyticWrapper: React.FC<CardAnalyticWrapperProps> = ({
  title,
  tooltip,
  badge,
  positionMetricShowing,
  isEmptyMetricData,
  onTitleClick,
  headerContent,
  children,
}) => {
  const { t } = useTranslation();
  const { value: active, toggle: toggleActive } = useToggle(false);
  const { setFalse: setFalseHover, setTrue: setTrueHover, value: isHover } = useToggle(false);

  const isShowSwitchMetric = positionMetricShowing !== undefined && (isHover || active);

  return (
    <div
      className={cls('relative', { 'GAnalytic-Badge-Logo': !isEmptyMetricData })}
      onMouseEnter={setTrueHover}
      onMouseLeave={setFalseHover}
    >
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="200">
            <InlineStack gap="200" align="space-between">
              <InlineStack gap="200">
                <div
                  className="hover:cursor-pointer hover:text-[--p-color-text-link-hover]"
                  onClick={onTitleClick}
                >
                  <InlineStack align="center">
                    <GTooltipCard tooltip={tooltip}>
                      <Text as="h3" variant="headingSm">
                        {title}
                      </Text>
                    </GTooltipCard>
                    {isHover && <Icon source={ChevronRightIcon} tone="inherit" />}
                  </InlineStack>
                </div>
                {badge?.isNew && <Badge tone="info">{t('New')}</Badge>}
              </InlineStack>
            </InlineStack>

            {headerContent}
          </BlockStack>

          {children}
        </BlockStack>
      </Card>

      {isShowSwitchMetric && (
        <Box position="absolute" insetBlockStart="300" insetInlineEnd="300">
          <AnalyticSwitchMetric
            active={active}
            toggleActive={toggleActive}
            positionMetricShowing={positionMetricShowing}
          />
        </Box>
      )}
    </div>
  );
};
